import { Component, OnInit } from "@angular/core";
import { ExercisesControllerService } from "../../core/api/controllers/exercises-controller/exercises-controller.service";
import { MediaControllerService } from "../../core/api/controllers/media-controller.service";
import { NotificationService } from "../../core/services/notification.service";
import { MixpanelService } from "../../core/services/mixpanel.service";
import { AuthService } from "../../core/services/auth.service";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MoveExercisesDialogComponent } from "../../shared/components/move-exercises-dialog/move-exercises-dialog.component";
import { PushExercisesToLocationDialog } from "./push-exercises-to-location-dialog/push-exercises-to-location-dialog.component";

@Component({
  selector: "app-exercises",
  templateUrl: "./exercises.component.html",
  styleUrls: ["./exercises.component.scss"],
  providers: [ExercisesControllerService, MediaControllerService],
})
export class ExercisesComponent implements OnInit {
  exerciseCategories: any[] = [];
  exercises: Record<string, any[]> = {};
  active = true;
  isLoading = true;
  toggle = true;
  categoriesLoaded = false;
  selectedExercises = [];
  selectedCategories = [];
  destinationCategory;
  searchQuery = "";
  collapsedCategoryIds = new Set<string>();
  private hasStoredCollapsedCategories = false;

  constructor(
    private exerciseService: ExercisesControllerService,
    private exercisesController: ExercisesControllerService,
    private notificationService: NotificationService,
    private mixpanelService: MixpanelService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.mixpanelService.track("exercises_screen", {});
    this.restoreCollapsedCategories();
    this.getCategories();
  }

  getCategories() {
    this.isLoading = true;
    this.exercisesController.getCategories().subscribe(
      (response) => {
        this.exerciseCategories = response;
        this.exercises = {};
        this.categoriesLoaded = true;
        this.isLoading = false;

        response.forEach((category) => {
          category.exercises = category.exercises.map((exercise) => ({
            ...exercise,
            categoryId: category.id,
          }));
          this.exercises[category.id] = category.exercises.sort((a, b) => {
            const aName =
              this.getNestedProperty(a, "regularExercise.name") ||
              this.getNestedProperty(a, "pairedExercise.firstExercise.name") ||
              this.getNestedProperty(a, "pairedExercise.secondExercise.name");
            const bName =
              this.getNestedProperty(b, "regularExercise.name") ||
              this.getNestedProperty(b, "pairedExercise.firstExercise.name") ||
              this.getNestedProperty(b, "pairedExercise.secondExercise.name");

            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
          });
        });

        this.initializeCollapsedCategories(response);
      },
      (error) => {
        this.notificationService.error(error);
        this.categoriesLoaded = true;
      },
    );
  }

  getNestedProperty(obj, path) {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  }

  setActiveAll() {
    let index: number;
    let categoryId;
    for (const exercise of this.selectedExercises) {
      for (const category of this.exerciseCategories) {
        index = this.exercises[category.id].indexOf(exercise);
        if (index > -1) {
          categoryId = category.id;
          break;
        }
      }
      this.setActive(exercise, categoryId);
    }
    this.finishAction(
      `Exercises successfully ${this.active ? "disabled" : "enabled"}.`,
    );
  }

  finishAction(message: string) {
    this.notificationService.success(message);
    this.selectedExercises = [];
  }

  setActive(exercise, categoryId) {
    this.exerciseService
      .setField({ id: exercise.id, field: "active", value: !exercise.active })
      .subscribe(
        () => {
          if (this.exercises[categoryId].length === 1) {
            this.removeExerciseCategory(categoryId);
          } else {
            this.exercises[categoryId].splice(
              this.exercises[categoryId].indexOf(exercise),
              1,
            );
          }
          this.notificationService.success(
            `Exercise successfully ${this.active ? "disabled" : "enabled"}.`,
          );
        },
        (error) => this.notificationService.error(error),
      );
  }

  openMoveModal() {
    this.destinationCategory = null;
    const data = {
      destinationCategory: this.destinationCategory,
      exercisesSelected: this.selectedExercises,
      exerciseCategories: this.exerciseCategories,
      exercises: this.exercises,
    };
    this.dialog
      .open(MoveExercisesDialogComponent, {
        data,
        width: "800px",
        maxWidth: "90vw",
        maxHeight: "90vh",
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getCategories();
        }
      });
  }

  updateSelectedExercise(ex) {
    const exercise = this.getExerciseProps(ex);
    const intersection = this.selectedExercises.find(
      ({ id }) => id === exercise.id,
    );
    if (intersection) {
      this.selectedExercises.splice(
        this.selectedExercises.indexOf(intersection),
        1,
      );
    } else {
      this.selectedExercises.push(exercise);
    }
  }

  removeExerciseCategory(categoryId) {
    for (let i = 0; i < this.exerciseCategories.length; i++) {
      if (this.exerciseCategories[i].id === categoryId) {
        this.exerciseCategories.splice(i, 1);
        break;
      }
    }
  }

  getExerciseProps(exercise) {
    return {
      ...(exercise.regularExercise || exercise.pairedExercise),
      categoryId: exercise.categoryId,
    };
  }

  isPairedExercise(exercise) {
    return (
      Boolean(exercise.pairedExercise) &&
      Boolean(exercise.pairedExercise.firstExercise) &&
      Boolean(exercise.pairedExercise.secondExercise)
    );
  }

  openPushExercisesDialog(): void {
    const data = {
      exercisesIds: this.selectedExercises.map((exercise) => exercise.id),
    };
    this.dialog
      .open(PushExercisesToLocationDialog, {
        panelClass: "location-picker-dialog-panel",
        maxHeight: "88vh",
        height: "88vh",
        width: "94vw",
        disableClose: true,
        maxWidth: "880px",
        data,
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.selectedExercises = [];
            this.selectedCategories = [];
          }
        },
      });
  }

  addToSingleLocation(ex: any, categoryId: string, updateVideos = null): void {
    const queryParams = {};
    const exercise = this.getExerciseProps(ex);
    if (exercise) {
      Object.assign(queryParams, {
        exerciseId: exercise.id,
        exercisePaired: this.isPairedExercise(exercise),
      });
    }

    if (categoryId) {
      const currentSelectedExercises = this.selectedExercises.filter(
        (item) => item.categoryId === categoryId,
      );
      if (!currentSelectedExercises.length) {
        return;
      }

      Object.assign(queryParams, {
        exerciseId: currentSelectedExercises.map((item) => item.id),
      });
    }

    if (updateVideos) {
      Object.assign(queryParams, { updateVideos });
    }

    this.router.navigate(["/sync-exercise"], { queryParams });
  }

  isSuperAdmin() {
    return this.authService.checkTokenAccess("SuperAdmin");
  }

  addCategory(categoryId) {
    const categoryExercises = this.exercises[categoryId] || [];
    const categoryExerciseIds = categoryExercises.map(
      (item) => this.getExerciseProps(item).id,
    );
    const selectedIds = new Set(this.selectedExercises.map(({ id }) => id));
    const shouldDeselect =
      categoryExerciseIds.length > 0 &&
      categoryExerciseIds.every((id) => selectedIds.has(id));

    if (shouldDeselect) {
      this.selectedExercises = this.selectedExercises.filter(
        ({ id }) => !categoryExerciseIds.includes(id),
      );
      this.selectedCategories = this.selectedCategories.filter(
        (id) => id !== categoryId,
      );
      return;
    }

    categoryExercises.forEach((item) => {
      const exercise = this.getExerciseProps(item);
      if (!selectedIds.has(exercise.id)) {
        this.selectedExercises.push(exercise);
        selectedIds.add(exercise.id);
      }
    });

    if (!this.selectedCategories.includes(categoryId)) {
      this.selectedCategories.push(categoryId);
    }
  }

  hasExercisesSelected(categoryId: string): boolean {
    return (
      this.selectedExercises.filter(
        (exercise) => exercise.categoryId === categoryId,
      ).length > 0
    );
  }

  isCategoryFullySelected(categoryId: string): boolean {
    const categoryExercises = this.exercises[categoryId] || [];
    if (!categoryExercises.length) return false;

    const selectedIds = new Set(this.selectedExercises.map(({ id }) => id));
    return categoryExercises.every((exercise) =>
      selectedIds.has(this.getExerciseProps(exercise).id),
    );
  }

  isCategoryPartiallySelected(categoryId: string): boolean {
    return (
      this.hasExercisesSelected(categoryId) &&
      !this.isCategoryFullySelected(categoryId)
    );
  }

  get totalExercises(): number {
    return Object.values(this.exercises).reduce(
      (total, categoryExercises) => total + categoryExercises.length,
      0,
    );
  }

  get visibleCategories(): any[] {
    if (!this.hasSearchQuery) return this.exerciseCategories;

    return this.exerciseCategories.filter(
      (category) => this.getVisibleExerciseCount(category) > 0,
    );
  }

  get visibleExerciseTotal(): number {
    return this.visibleCategories.reduce(
      (total, category) => total + this.getVisibleExerciseCount(category),
      0,
    );
  }

  get hasSearchQuery(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  getVisibleExerciseCount(category: any): number {
    const categoryExercises = this.exercises[category.id] || [];
    if (!this.hasSearchQuery || this.categoryMatchesSearch(category)) {
      return categoryExercises.length;
    }

    const query = this.normalizedSearchQuery;
    return categoryExercises.filter((exercise) =>
      this.getExerciseName(exercise).toLocaleLowerCase().includes(query),
    ).length;
  }

  clearSearch(): void {
    this.searchQuery = "";
  }

  toggleCategory(categoryId: string): void {
    if (this.collapsedCategoryIds.has(categoryId)) {
      this.collapsedCategoryIds.delete(categoryId);
    } else {
      this.collapsedCategoryIds.add(categoryId);
    }

    this.collapsedCategoryIds = new Set(this.collapsedCategoryIds);
    this.persistCollapsedCategories();
  }

  isCategoryCollapsed(categoryId: string): boolean {
    return this.collapsedCategoryIds.has(categoryId);
  }

  isCategoryContentVisible(categoryId: string): boolean {
    return this.hasSearchQuery || !this.isCategoryCollapsed(categoryId);
  }

  trackByCategoryId(_index: number, category: any): string {
    return category.id;
  }

  private get normalizedSearchQuery(): string {
    return this.searchQuery.trim().toLocaleLowerCase();
  }

  private categoryMatchesSearch(category: any): boolean {
    return category.name
      ?.toLocaleLowerCase()
      .includes(this.normalizedSearchQuery);
  }

  private getExerciseName(exercise: any): string {
    return (
      exercise.regularExercise?.name ||
      exercise.pairedExercise?.name ||
      exercise.pairedExercise?.firstExercise?.name ||
      exercise.pairedExercise?.secondExercise?.name ||
      ""
    );
  }

  private restoreCollapsedCategories(): void {
    const storageKey = AuthService.EXERCISES_COLLAPSED_CATEGORIES_STORAGE_KEY;
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) return;

    try {
      const storedState = JSON.parse(storedValue);
      if (
        storedState.locationId !== AuthService.getSelectedLocationId() ||
        !Array.isArray(storedState.categoryIds)
      ) {
        localStorage.removeItem(storageKey);
        return;
      }

      this.collapsedCategoryIds = new Set(storedState.categoryIds);
      this.hasStoredCollapsedCategories = true;
    } catch {
      localStorage.removeItem(storageKey);
    }
  }

  private initializeCollapsedCategories(categories: any[]): void {
    if (this.hasStoredCollapsedCategories) return;

    this.collapsedCategoryIds = new Set(
      categories.map((category) => category.id),
    );
    this.hasStoredCollapsedCategories = true;
    this.persistCollapsedCategories();
  }

  private persistCollapsedCategories(): void {
    localStorage.setItem(
      AuthService.EXERCISES_COLLAPSED_CATEGORIES_STORAGE_KEY,
      JSON.stringify({
        locationId: AuthService.getSelectedLocationId(),
        categoryIds: Array.from(this.collapsedCategoryIds),
      }),
    );
  }
}
